import { Code, ConnectError } from '@connectrpc/connect'
import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import {
  ContractorErrorReason,
  ContractorErrorReasonSchema,
  CustomerSupplyPointErrorReason,
  CustomerSupplyPointErrorReasonSchema,
} from '@ebalance/proto/bffuser/v1/customer_model_pb'

import { readAppErrorInfo } from '~/libs/appErrorInfo'
import {
  ContractorError,
  type ContractorErrorReason as ContractorReason,
  SupplyPointError,
  type SupplyPointErrorParams,
  type SupplyPointErrorReason as SupplyPointReason,
} from '~/pages/CustomerInfo/models/supplyPointError'

/**
 * backend の拒否理由 (`google.rpc.ErrorInfo` の reason) を、 画面の表示と欄の振り分けが
 * 引く reason (`./supplyPointError.ts`) に写す。 enum の全値を持たせて、 reason を足した時に
 * ここが型エラーになるようにする。 `undefined` は専用の文言を持たない値 (汎用文言に落ちる)。
 */
const SUPPLY_POINT_REASONS: Record<
  CustomerSupplyPointErrorReason,
  SupplyPointReason | undefined
> = {
  [CustomerSupplyPointErrorReason.UNSPECIFIED]: undefined,
  [CustomerSupplyPointErrorReason.CUSTOMER_REQUIRED]: 'CUSTOMER_REQUIRED',
  [CustomerSupplyPointErrorReason.CUSTOMER_NAME_REQUIRED]:
    'CUSTOMER_NAME_REQUIRED',
  [CustomerSupplyPointErrorReason.CUSTOMER_NAME_TOO_LONG]:
    'CUSTOMER_NAME_TOO_LONG',
  [CustomerSupplyPointErrorReason.CONTRACT_KW_NOT_POSITIVE]:
    'CONTRACT_KW_NOT_POSITIVE',
  [CustomerSupplyPointErrorReason.END_BEFORE_START]: 'END_BEFORE_START',
  [CustomerSupplyPointErrorReason.SUPPLY_POINT_NUMBER_REQUIRED]:
    'SUPPLY_POINT_NUMBER_REQUIRED',
  [CustomerSupplyPointErrorReason.SUPPLY_POINT_NUMBER_INVALID]:
    'SUPPLY_POINT_NUMBER_INVALID',
  [CustomerSupplyPointErrorReason.SUPPLY_POINT_NUMBER_IMMUTABLE]:
    'SUPPLY_POINT_NUMBER_IMMUTABLE',
  [CustomerSupplyPointErrorReason.PERIOD_OVERLAP]: 'PERIOD_OVERLAP',
  [CustomerSupplyPointErrorReason.RETAILER_REQUIRED]: 'RETAILER_REQUIRED',
  [CustomerSupplyPointErrorReason.RETAILER_NOT_IN_AREA]: 'RETAILER_NOT_IN_AREA',
  [CustomerSupplyPointErrorReason.RETAILER_UNAVAILABLE_IN_AREA]:
    'RETAILER_UNAVAILABLE_IN_AREA',
  [CustomerSupplyPointErrorReason.PRIVATE_GENERATION_CAPACITY_REQUIRED]:
    'PRIVATE_GENERATION_CAPACITY_REQUIRED',
  [CustomerSupplyPointErrorReason.REVERSE_POWER_FLOW_REQUIRED]:
    'REVERSE_POWER_FLOW_REQUIRED',
  [CustomerSupplyPointErrorReason.REVERSE_POWER_FLOW_OUT_OF_RANGE]:
    'REVERSE_POWER_FLOW_OUT_OF_RANGE',
  [CustomerSupplyPointErrorReason.STAGE_TRANSITION_NOT_ALLOWED]:
    'STAGE_TRANSITION_NOT_ALLOWED',
  [CustomerSupplyPointErrorReason.CONTRACT_IN_USE]: 'CONTRACT_IN_USE',
  [CustomerSupplyPointErrorReason.ACTUALS_CHECK_UNAVAILABLE]:
    'ACTUALS_CHECK_UNAVAILABLE',
  [CustomerSupplyPointErrorReason.CONFLICT]: 'CONFLICT',
  [CustomerSupplyPointErrorReason.CONTRACTOR_REQUIRED]: 'CONTRACTOR_REQUIRED',
  [CustomerSupplyPointErrorReason.CUSTOMER_AREA_MISMATCH]:
    'CUSTOMER_AREA_MISMATCH',
  [CustomerSupplyPointErrorReason.CUSTOMER_NUMBER_MISMATCH]:
    'CUSTOMER_NUMBER_MISMATCH',
  [CustomerSupplyPointErrorReason.SUPPLY_POINT_NUMBER_NOT_EDITABLE]:
    'SUPPLY_POINT_NUMBER_NOT_EDITABLE',
  [CustomerSupplyPointErrorReason.SUPPLY_POINT_NUMBER_AREA_MISMATCH]:
    'SUPPLY_POINT_NUMBER_AREA_MISMATCH',
  [CustomerSupplyPointErrorReason.KW_PERIOD_INVALID]: 'KW_PERIOD_INVALID',
  [CustomerSupplyPointErrorReason.KW_PERIODS_TOO_MANY]: 'KW_PERIODS_TOO_MANY',
  [CustomerSupplyPointErrorReason.RETAILER_NOT_CHANGEABLE]:
    'RETAILER_NOT_CHANGEABLE',
}

const CONTRACTOR_REASONS: Record<
  ContractorErrorReason,
  ContractorReason | undefined
> = {
  [ContractorErrorReason.UNSPECIFIED]: undefined,
  [ContractorErrorReason.NAME_REQUIRED]: 'NAME_REQUIRED',
  [ContractorErrorReason.NAME_TOO_LONG]: 'NAME_TOO_LONG',
  [ContractorErrorReason.POSTAL_CODE_TOO_LONG]: 'POSTAL_CODE_TOO_LONG',
  [ContractorErrorReason.ADDRESS_TOO_LONG]: 'ADDRESS_TOO_LONG',
  [ContractorErrorReason.ATTENTION_TOO_LONG]: 'ATTENTION_TOO_LONG',
  [ContractorErrorReason.NOTE_TOO_LONG]: 'NOTE_TOO_LONG',
  [ContractorErrorReason.HAS_CUSTOMERS]: 'HAS_CUSTOMERS',
}

/** backend の `area_code` metadata (エリアの小文字名) と AreaCode の対応。 */
const AREA_BY_TOKEN: Record<string, AreaCode> = {
  hokkaido: AreaCode.HOKKAIDO,
  tohoku: AreaCode.TOHOKU,
  tokyo: AreaCode.TOKYO,
  chubu: AreaCode.CHUBU,
  hokuriku: AreaCode.HOKURIKU,
  kansai: AreaCode.KANSAI,
  chugoku: AreaCode.CHUGOKU,
  shikoku: AreaCode.SHIKOKU,
  kyushu: AreaCode.KYUSHU,
  okinawa: AreaCode.OKINAWA,
}

const paramsOf = (metadata: Record<string, string>): SupplyPointErrorParams => {
  const areaCode = AREA_BY_TOKEN[metadata['area_code'] ?? '']
  return {
    supplyPointNumber: metadata['supply_point_number'],
    customerName: metadata['customer_name'],
    numberBlank: metadata['number_blank'] === 'true',
    fixOthers: metadata['fix_others'] === 'true',
    ...(areaCode === undefined ? {} : { areaCode }),
  }
}

/**
 * 地点の操作が返したエラーを `SupplyPointError` にする。 ErrorInfo の reason を持たない
 * エラーは、 競合 (Aborted) だけ `CONFLICT` に寄せ、 他はそのまま返す (汎用文言)。
 */
export const toSupplyPointError = (err: unknown): unknown => {
  if (err instanceof SupplyPointError || !(err instanceof ConnectError)) {
    return err
  }
  const info = readAppErrorInfo(err, CustomerSupplyPointErrorReasonSchema)
  const reason =
    info === undefined ? undefined : SUPPLY_POINT_REASONS[info.reason]
  if (info !== undefined && reason !== undefined) {
    return new SupplyPointError(reason, paramsOf(info.metadata))
  }
  return err.code === Code.Aborted ? new SupplyPointError('CONFLICT') : err
}

/** 契約者の操作が返したエラーを `ContractorError` にする。 reason を持たないエラーはそのまま返す。 */
export const toContractorError = (err: unknown): unknown => {
  if (err instanceof ContractorError || !(err instanceof ConnectError)) {
    return err
  }
  const info = readAppErrorInfo(err, ContractorErrorReasonSchema)
  const reason =
    info === undefined ? undefined : CONTRACTOR_REASONS[info.reason]
  if (info === undefined || reason === undefined) return err
  const count = Number(info.metadata['customer_count'])
  return new ContractorError(
    reason,
    Number.isFinite(count) ? { customerCount: count } : {},
  )
}
